/**
 * T073: Tag Multi-Select Component
 * Multi-select for choosing tags
 */

import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTags } from '@/hooks/useTags'
import { Skeleton } from '@/components/ui/skeleton'

interface TagMultiSelectProps {
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
}

export function TagMultiSelect({ value, onChange, disabled }: TagMultiSelectProps) {
  const { data, isLoading } = useTags()
  const tags = data?.data ?? []

  const selectedTags = tags.filter((tag) => value.includes(tag.id))
  const availableTags = tags.filter((tag) => !value.includes(tag.id))

  const handleAdd = (tagId: string) => {
    if (tagId && !value.includes(tagId)) {
      onChange([...value, tagId])
    }
  }

  const handleRemove = (tagId: string) => {
    onChange(value.filter((id) => id !== tagId))
  }

  if (isLoading) {
    return <Skeleton className="h-10 w-full" />
  }

  return (
    <div className="space-y-2">
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedTags.map((tag) => (
            <Badge key={tag.id} variant="secondary" className="gap-1">
              {tag.name}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0 hover:bg-transparent"
                onClick={() => handleRemove(tag.id)}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Remove {tag.name}</span>
              </Button>
            </Badge>
          ))}
        </div>
      )}
      {availableTags.length > 0 && (
        <Select onValueChange={handleAdd} disabled={disabled} value="">
          <SelectTrigger>
            <SelectValue placeholder="Add a tag..." />
          </SelectTrigger>
          <SelectContent>
            {availableTags.map((tag) => (
              <SelectItem key={tag.id} value={tag.id}>
                {tag.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {tags.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No tags available. Create tags in Settings.
        </p>
      )}
    </div>
  )
}
